import React from 'react'
import { Box, IconButton, Typography, styled } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'

interface ModernQuantityPickerProps {
	value: number
	min?: number
	max?: number
	size?: 'small' | 'medium'
	onChange: (newValue: number) => void
	disabled?: boolean
}

// Styled container for the "pill" look
const StyledContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'small' | 'medium' }>(({ theme, size }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: size === 'small' ? '8px' : '12px',
	padding: size === 'small' ? '2px' : '4px',
	backgroundColor: theme.palette.background.paper,
	transition: 'all 0.2s ease-in-out',
	'&:hover': {
		borderColor: theme.palette.primary.main,
		boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
	},
}))

const ModernQuantityPicker: React.FC<ModernQuantityPickerProps> = ({
	value,
	min = 1,
	max = 99,
	size = 'medium',
	onChange,
	disabled = false,
}) => {
	const handleDecrement = () => {
		if (value > min) onChange(value - 1)
	}

	const handleIncrement = () => {
		if (value < max) onChange(value + 1)
	}

	// Size-specific dimensions
	const iconSize = size === 'small' ? 14 : 18
	const buttonPadding = size === 'small' ? 0.5 : 1
	const width = size === 'small' ? 70 : 110
	const fontSize = size === 'small' ? '0.75rem' : '0.9rem'

	return (
		<StyledContainer size={size} sx={{ width }}>
			<IconButton
				size="small"
				onClick={handleDecrement}
				disabled={value <= min || disabled}
				sx={{
					p: buttonPadding,
					borderRadius: size === 'small' ? '4px' : '8px',
					color: 'text.secondary',
				}}
			>
				<RemoveIcon sx={{ fontSize: iconSize }} />
			</IconButton>

			<Typography
				variant="body1"
				sx={{
					fontWeight: 600,
					fontSize: fontSize,
					minWidth: '20px',
					textAlign: 'center',
					userSelect: 'none',
				}}
			>
				{value}
			</Typography>

			<IconButton
				size="small"
				onClick={handleIncrement}
				disabled={value >= max || disabled}
				sx={{
					p: buttonPadding,
					borderRadius: size === 'small' ? '4px' : '8px',
					color: 'primary.main',
				}}
			>
				<AddIcon sx={{ fontSize: iconSize }} />
			</IconButton>
		</StyledContainer>
	)
}

export default ModernQuantityPicker
